import Decimal from 'decimal.js'

// Calculate prices and counts for cart items

/**
 * Calculate total extra price from selections for a single item
 * @param {Array} selections
 * @returns {number}
 */
export const calculateSelectionsExtra = selections => {
    if (!Array.isArray(selections)) return 0
    const total = selections.reduce((accSelections, selection) => {
        const selectedOptions = selection?.options || []
        const selectionTotal = selectedOptions.reduce((optionTotal, option) => {
            const price = new Decimal(option?.sellPrice || 0)
            const quantity = new Decimal(option?.quantity || 0)
            return optionTotal.plus(price.times(quantity))
        }, new Decimal(0))
        return accSelections.plus(selectionTotal)
    }, new Decimal(0))
    return total.toNumber()
}

/**
 * Calculate total amount and item count for a single cart item
 * @param {Object} item
 * @returns {{amount:number, count:number}}
 */
export const calculateItemTotals = item => {
    if (!item) return { amount: 0, count: 0 }
    const selectionPrice = new Decimal(calculateSelectionsExtra(item.selections))

    const { amount, count } = (item.variants || []).reduce(
        (acc, variant) => {
            const quantity = new Decimal(variant?.quantity || 0)
            const sellPrice = new Decimal(variant?.sellPrice || 0)
            const itemTotal = sellPrice.plus(selectionPrice).times(quantity)
            return {
                amount: acc.amount.plus(itemTotal),
                count: acc.count.plus(quantity),
            }
        },
        { amount: new Decimal(0), count: new Decimal(0) }
    )
    return { amount: amount.toNumber(), count: count.toNumber() }
}

/**
 * Calculate totals for a list of cart items
 * @param {Array} items
 * @param {number} creditToUseCents - Amount of credit to use for discount (in cents)
 * @param {number} discountPct - Discount percentage from member benefit (0-100), defaults to 0
 * @param {boolean} isComplimentaryOrder - Whether order is complimentary (free of charge)
 * @param {number} promoDiscount - Promo discount amount (in dollars), defaults to 0
 * @returns {{amount:number, count:number, discountAmount:number, roleDiscountAmount:number, promoDiscountAmount:number, finalAmount:number}}
 */
export const calculateCartTotals = (
    items,
    creditToUseCents = 0,
    discountPct = 0,
    isComplimentaryOrder = false,
    promoDiscount = 0
) => {
    if (!Array.isArray(items) || items.length === 0) {
        return { amount: 0, count: 0, discountAmount: 0, roleDiscountAmount: 0, promoDiscountAmount: 0, finalAmount: 0 }
    }

    const { amount: amountDecimal, count: countDecimal } = items.reduce(
        (acc, item) => {
            const { amount: itemAmount, count: itemCount } = calculateItemTotals(item)
            return {
                amount: acc.amount.plus(new Decimal(itemAmount)),
                count: acc.count.plus(new Decimal(itemCount)),
            }
        },
        { amount: new Decimal(0), count: new Decimal(0) }
    )

    const amount = amountDecimal.toNumber()
    const count = countDecimal.toNumber()

    // If complimentary order, total is $0.00
    if (isComplimentaryOrder) {
        return { amount, count, discountAmount: amount, roleDiscountAmount: 0, promoDiscountAmount: 0, finalAmount: 0 }
    }

    // Apply benefit discount if discountPct > 0
    // discountPct is a percentage (0-100), convert to decimal rate
    let amountAfterRoleDiscount = new Decimal(amount)
    let roleDiscountAmount = new Decimal(0)
    if (discountPct > 0 && discountPct <= 100) {
        const discountRate = new Decimal(discountPct).div(100)
        roleDiscountAmount = new Decimal(amount).times(discountRate)
        amountAfterRoleDiscount = new Decimal(amount).times(new Decimal(1).minus(discountRate))
    }

    // Apply promo discount (after role discount, before credit)
    const promoDiscountDecimal = new Decimal(promoDiscount)
    const amountAfterPromoDiscount = Decimal.max(0, amountAfterRoleDiscount.minus(promoDiscountDecimal))

    // Convert credit from cents to dollars
    const creditToUseDollars = new Decimal(creditToUseCents).div(100)
    // Credit discount applies to amount after role and promo discounts
    const discountAmount = Decimal.min(creditToUseDollars, amountAfterPromoDiscount)
    const finalAmount = Decimal.max(0, amountAfterPromoDiscount.minus(discountAmount))

    return {
        amount,
        count,
        discountAmount: discountAmount.toNumber(),
        roleDiscountAmount: roleDiscountAmount.toNumber(),
        promoDiscountAmount: promoDiscountDecimal.toNumber(),
        finalAmount: finalAmount.toNumber(),
    }
}

/**
 * Calculate maximum credit that can be used based on order amount and available credit
 * Business rules:
 * 1. Customer must pay at least stripe minimum ($0.50)
 * 2. Payable amount must be rounded to 10 cents (for cash payment compatibility)
 * 3. Maximum credit = min(theoreticalMax, availableCredit, adjustedCredit)
 *
 * Examples:
 * - order = $12.40 (1240 cents), available = 2000 cents
 *   → theoretical max = 1240 - 50 = 1190 cents
 *   → max credit = min(1190, 2000) = 1190 cents (customer pays $0.50)
 * - order = $12.40 (1240 cents), available = 1000 cents (< 1190)
 *   → max credit = 1000 cents (customer pays $2.40, which is already in 10 cents)
 *
 * @param {number} orderAmountAfterDiscount - Order amount after benefit discount (in dollars)
 * @param {number} availableCreditCents - Available credit balance (in cents)
 * @param {number} stripeMinimumCents - Stripe minimum payment amount (in cents), default 50
 * @param {number} cashUnitCents - Cash payment unit (in cents), default 10
 * @returns {number} - Maximum credit to use (in cents)
 */
export const calculateMaxCreditToUse = (
    orderAmountAfterDiscount,
    availableCreditCents,
    stripeMinimumCents = 50,
    cashUnitCents = 10
) => {
    const orderAmountDecimal = new Decimal(orderAmountAfterDiscount)
    const orderAmountCents = orderAmountDecimal.times(100).round()
    const availableCreditDecimal = new Decimal(availableCreditCents)
    const stripeMinimumDecimal = new Decimal(stripeMinimumCents)
    const cashUnitDecimal = new Decimal(cashUnitCents)

    // Case 1: credit is enough to fully cover the order — allow full free
    if (availableCreditDecimal.gte(orderAmountCents)) {
        return orderAmountCents.toNumber()
    }

    // Case 2: partial credit — must ensure minimum payable and 10 cents rounding
    const theoreticalMaxCredit = orderAmountCents.minus(stripeMinimumDecimal)

    // Case 2a: available credit is less than or equal to max allowed
    if (availableCreditDecimal.lte(theoreticalMaxCredit)) {
        const payableIfUseAll = orderAmountCents.minus(availableCreditDecimal)

        // Check if payable amount is already a multiple of cash unit
        if (payableIfUseAll.mod(cashUnitDecimal).isZero()) {
            return availableCreditCents
        }

        // Round up to next 10-cent multiple to ensure cash compatibility
        let payable = payableIfUseAll.div(cashUnitDecimal).ceil().times(cashUnitDecimal)
        if (payable.lt(stripeMinimumDecimal)) {
            payable = stripeMinimumDecimal
        }

        const creditToUse = orderAmountCents.minus(payable)
        const maxCredit = Decimal.max(0, Decimal.min(creditToUse, availableCreditDecimal))
        return maxCredit.toNumber()
    }

    // Case 2b: available credit > max, cap it to ensure stripe minimum
    return theoreticalMaxCredit.toNumber()
}

/**
 * Calculate adjusted credit to use based on desired credit and order amount
 * Ensures that payable amount is at least stripe minimum (if applicable) and is rounded to 10 cents
 * If available credit >= order amount, allows full order amount ($0.00 payable)
 * @param {number} orderAmountAfterDiscount - Order amount after benefit discount (in dollars)
 * @param {number} desiredCreditCents - Desired credit to use (in cents)
 * @param {number} availableCreditCents - Available credit balance (in cents)
 * @param {number} stripeMinimumCents - Stripe minimum payment amount (in cents), default 50
 * @param {number} cashUnitCents - Cash payment unit (in cents), default 10
 * @returns {number} - Adjusted credit to use (in cents)
 */
export const calculateAdjustedCreditToUse = (
    orderAmountAfterDiscount,
    desiredCreditCents,
    availableCreditCents,
    stripeMinimumCents = 50,
    cashUnitCents = 10
) => {
    const orderAmountDecimal = new Decimal(orderAmountAfterDiscount)
    const orderAmountCents = orderAmountDecimal.times(100).round()

    const availableCreditDecimal = new Decimal(availableCreditCents)
    const stripeMinimumDecimal = new Decimal(stripeMinimumCents)
    const cashUnitDecimal = new Decimal(cashUnitCents)
    const desiredCreditDecimal = new Decimal(desiredCreditCents)

    // Clamp desired credit between 0 and available credit
    let creditToUse = Decimal.max(0, Decimal.min(desiredCreditDecimal, availableCreditDecimal))

    // Calculate payable amount if we use this credit
    let payableAmount = orderAmountCents.minus(creditToUse)

    // If available credit is enough to cover full order amount, allow $0.00 payable
    if (availableCreditDecimal.gte(orderAmountCents)) {
        // Can use full order amount, payable amount can be $0.00
        // If payable amount is 0 or negative, allow full order amount (complete free)
        if (payableAmount.lte(0)) {
            return orderAmountCents.toNumber()
        }
        // If payable amount > 0, round to 10 cents for cash payment compatibility
        // Round payable amount up to nearest 10 cents
        const roundedPayableAmount = payableAmount.div(cashUnitDecimal).ceil().times(cashUnitDecimal)
        creditToUse = orderAmountCents.minus(roundedPayableAmount)
        // Ensure credit doesn't exceed order amount (can't use more than order)
        const maxCredit = Decimal.max(0, Decimal.min(creditToUse, orderAmountCents))
        return maxCredit.toNumber()
    }

    // If available credit < order amount, ensure payable amount is at least stripe minimum
    if (payableAmount.lt(stripeMinimumDecimal)) {
        // Adjust credit to ensure payable amount is at least stripe minimum
        creditToUse = orderAmountCents.minus(stripeMinimumDecimal)
        payableAmount = stripeMinimumDecimal
    } else {
        // Round payable amount up to nearest 10 cents to ensure cash payment compatibility
        const roundedPayableAmount = payableAmount.div(cashUnitDecimal).ceil().times(cashUnitDecimal)
        creditToUse = orderAmountCents.minus(roundedPayableAmount)
        payableAmount = roundedPayableAmount
    }

    // Ensure credit to use doesn't exceed available credit and is not negative
    const finalCredit = Decimal.max(0, Decimal.min(creditToUse, availableCreditDecimal))
    return finalCredit.toNumber()
}
